import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Snackbar, Alert } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { styled } from '@mui/material/styles';
import ThemeBackground from '../../components/Layout/ThemeBackground';
import { uploadFile } from '../../services/uploadService';
import { fetchPublicLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup';
import AvailabilitySlotEditor, { AvailabilitySlot } from '../../components/shared/AvailabilitySlotEditor';
import api from '../../utils/api';
import { toDateOnlyString } from '../../utils/dateUtils';

const DatePickerWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  '& .react-datepicker-popper': {
    zIndex: 9999,
  },
  '& .datepicker-popper': {
    zIndex: 9999,
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    zIndex: 9999,
  },
  '& .react-datepicker__header': {
    backgroundColor: theme.palette.grey[100],
  },
  '& .react-datepicker__day--selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '& .react-datepicker__day--keyboard-selected': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  '& .react-datepicker__day:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container': {
    width: '100%',
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '8px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '4px',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main
    }
  }
}));

const steps = ['Personal Info', 'Professional Info', 'Legal Documents'];

const nationalities = [
  'British', 'Irish', 'Other EU', 'American', 'Canadian', 'Australian', 
  'New Zealand', 'South African', 'Indian', 'Pakistani', 'Bangladeshi', 'Other'
];

const genderOptions = ['Male', 'Female', 'Non-binary', 'Prefer not to say', 'Other'];

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  gender: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  postcode: string;
  modality: string[];
  sessionTypes: string[];
  availability: string[];
  availabilitySlots: AvailabilitySlot[];
  experienceOrTraining: string[];
  languages: string[];
  nationality: string;
  workPermit: boolean;
  workPermitDetails?: string;
  workPermitDocument?: { name: string; url: string; originalName: string; };
  dbsCheck: boolean;
  dbsType?: 'certificate' | 'update_service';
  dbsNumber: string;
  dbsIssueDate?: string; // Issue/print date from certificate
  dbsExpiryDate?: string; // Calculated from issue date (3 years) - kept for Update Service
  dbsUpdateServiceId?: string;
  dbsUpdateServiceConsent?: boolean;
  legalFirstName?: string; // Legal name on DBS certificate (if different from preferred name)
  legalLastName?: string; // Legal surname on DBS certificate (if different from preferred name)
  dbsCertificate?: { name: string; url: string; originalName: string; };
  insuranceProvider: string;
  insuranceNumber: string;
  insuranceExpiryDate: string;
  insuranceCertificate?: { name: string; url: string; originalName: string; };
  onlineLearningCertificate?: { name: string; url: string; originalName: string; };
  trustIdDocument?: { name: string; url: string; originalName: string; };
  trustIdNotes?: string;
}

const AddExistingCounsellor: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | undefined>();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    gender: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    modality: [],
    sessionTypes: [],
    availability: [],
    availabilitySlots: [],
    experienceOrTraining: [],
    languages: [],
    nationality: '',
    workPermit: false,
    dbsCheck: false,
    dbsNumber: '',
    dbsIssueDate: '',
    insuranceProvider: '',
    insuranceNumber: '',
    insuranceExpiryDate: ''
  });

  const [modalityLookups, setModalityLookups] = useState<Lookup[]>([]);
  const [sessionTypesLookups, setSessionTypesLookups] = useState<Lookup[]>([]);
  const [availabilityLookups, setAvailabilityLookups] = useState<Lookup[]>([]);
  const [experienceTrainingLookups, setExperienceTrainingLookups] = useState<Lookup[]>([]);
  const [languageLookups, setLanguageLookups] = useState<Lookup[]>([]);
  
useEffect(() => {
    const loadLookups = async () => {
      try {
        const [modality, sessionTypes, availability, experienceTraining, languages] = await Promise.all([
          fetchPublicLookups('modality'),
          fetchPublicLookups('sessionTypes'),
          fetchPublicLookups('availability'),
          fetchPublicLookups('experienceTraining'),
          fetchPublicLookups('language')
        ]);
        
        setModalityLookups(modality);
        setSessionTypesLookups(sessionTypes);
        setAvailabilityLookups(availability);
        setExperienceTrainingLookups(experienceTraining);
        setLanguageLookups(languages);
      } catch (error) {
        console.error('Error loading lookups:', error);
      }
    };
    
    loadLookups();
  }, []);

  // Check if email exists
  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      // Check counsellors
      const counsellorResponse = await api.get(`/api/counsellors/check-email/${email}`);
      if (counsellorResponse.data.exists) {
        setApiError('Unable to add counsellor as this email address is already registered to an active counsellor');
        return true;
      }

      // Check users
      const userResponse = await api.get(`/api/users/check-email/${email}`);
      if (userResponse.data.exists) {
        setApiError('Unable to add counsellor as this email address is already registered to a user account');
        return true;
      }

      setApiError(undefined);
      return false;
    } catch (error: any) {
      console.error('Error checking email:', error);
      if (error.response?.data?.error) {
        setApiError(error.response.data.error);
      }
      // Don't block form submission on email check error
      return false;
    }
  };

  // Debounced email validation
  useEffect(() => {
    if (formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      const timer = setTimeout(() => {
        checkEmailExists(formData.email);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setApiError(undefined);
    }
  }, [formData.email]);

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Clear API error when user changes email
    if (field === 'email') {
      setApiError(undefined);
    }
  };

  const handleFileUpload = async (field: 'dbsCertificate' | 'insuranceCertificate' | 'onlineLearningCertificate' | 'trustIdDocument' | 'workPermitDocument', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadFile(file, {});
      
      handleChange(field, {
        name: result.fileName,
        url: result.fileUrl,
        originalName: file.name
      });

      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('File upload error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload file',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      } else if (apiError) {
        newErrors.email = apiError;
      }
      
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone is required';
      } else if (!/^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/.test(formData.phone.replace(/\s/g, ''))) {
        newErrors.phone = 'Please enter a valid UK mobile number';
      }
      
      if (!formData.dob) {
        newErrors.dob = 'Date of birth is required';
      } else {
        const age = Math.floor((new Date().getTime() - new Date(formData.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
        if (age < 18) {
          newErrors.dob = 'Counsellor must be at least 18 years old';
        }
      }
      if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.county.trim()) newErrors.county = 'County is required';
      if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    }

    if (step === 1) {
      if (formData.modality.length === 0) newErrors.modality = 'At least one modality is required';
      if (formData.sessionTypes.length === 0) newErrors.sessionTypes = 'At least one session type is required';
      // Availability is optional - detailed time slots are the primary method
    }

    if (step === 2) {
      if (!formData.nationality) newErrors.nationality = 'Nationality is required';
      
      if (formData.workPermit) {
        if (!formData.workPermitDetails?.trim()) newErrors.workPermitDetails = 'Work permit details are required';
        if (!formData.workPermitDocument) newErrors.workPermitDocument = 'Work permit document is required';
      }
      
      if (formData.dbsCheck) {
        if (!formData.dbsType) newErrors.dbsType = 'Please select DBS verification method';
        if (!formData.dbsNumber.trim()) newErrors.dbsNumber = 'DBS number is required';
        
        if (formData.dbsType === 'certificate') {
          if (!formData.dbsIssueDate) newErrors.dbsIssueDate = 'DBS issue date is required';
          if (!formData.dbsCertificate) newErrors.dbsCertificate = 'DBS certificate is required';
        }
        
        if (formData.dbsType === 'update_service') {
          if (!formData.dbsUpdateServiceId?.trim()) newErrors.dbsUpdateServiceId = 'DBS Update Service ID is required';
          if (!formData.dbsUpdateServiceConsent) newErrors.dbsUpdateServiceConsent = 'Consent is required for DBS Update Service';
        }
      }
      
      if (!formData.insuranceProvider.trim()) newErrors.insuranceProvider = 'Insurance provider is required';
      if (!formData.insuranceNumber.trim()) newErrors.insuranceNumber = 'Insurance policy number is required';
      if (!formData.insuranceExpiryDate) newErrors.insuranceExpiryDate = 'Insurance expiry date is required';
      if (!formData.insuranceCertificate) newErrors.insuranceCertificate = 'Insurance certificate is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/counsellors/add-existing', formData);
      
      setSnackbar({
        open: true,
        message: 'Existing counsellor added successfully. Trust ID verification pending.',
        severity: 'success'
      });

      setTimeout(() => {
        navigate(`/counsellor/${response.data.counsellorId}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error creating counsellor:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to create counsellor',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderFileUpload = (
    field: 'dbsCertificate' | 'insuranceCertificate' | 'onlineLearningCertificate' | 'trustIdDocument' | 'workPermitDocument',
    label: string,
    required: boolean = true
  ) => {
    const file = formData[field];
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium theme-text-primary mb-2">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        
        {file ? (
          <div className="flex items-center gap-2 p-2 border rounded-lg theme-input">
            <span className="flex-1 theme-text-primary">{file.originalName}</span>
            <button
              type="button"
              onClick={() => handleChange(field, undefined)}
              className="px-3 py-1 text-sm text-red-400 hover:text-red-300"
            >
              Remove
            </button>
          </div>
        ) : (
          <label className="block">
            <input
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileUpload(field, e)}
              disabled={uploading}
            />
            <div className="cursor-pointer p-3 border-2 border-dashed rounded-lg theme-input text-center hover:border-blue-400">
              <CloudUploadIcon className="mx-auto mb-2" />
              <span className="theme-text-primary">{uploading ? 'Uploading...' : `Upload ${label}`}</span>
            </div>
          </label>
        )}
        
        {errors[field] && (
          <p className="text-sm text-red-400 mt-1">{errors[field]}</p>
        )}
      </div>
    );
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">First Name <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                />
                {errors.firstName && <p className="text-sm text-red-400 mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Last Name <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                />
                {errors.lastName && <p className="text-sm text-red-400 mt-1">{errors.lastName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Email <span className="text-red-400">*</span></label>
                <input
                  type="email"
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
                {errors.email && <p className="text-sm text-red-400 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Phone <span className="text-red-400">*</span></label>
                <input
                  type="tel"
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="07XXX XXXXXX"
                />
                {errors.phone && <p className="text-sm text-red-400 mt-1">{errors.phone}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Date of Birth <span className="text-red-400">*</span></label>
                <DatePickerWrapper>
                  <DatePicker
                    selected={formData.dob ? new Date(formData.dob) : null}
                    onChange={(date) => handleChange('dob', date ? toDateOnlyString(date) : '')}
                    className="theme-input w-full p-2 border rounded-lg"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select date of birth"
                    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </DatePickerWrapper>
                {errors.dob && <p className="text-sm text-red-400 mt-1">{errors.dob}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Gender</label>
                <select
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                >
                  <option value="">Select gender</option>
                  {genderOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Address Line 1 <span className="text-red-400">*</span></label>
              <input
                type="text"
                className="theme-input w-full p-2 border rounded-lg"
                value={formData.addressLine1}
                onChange={(e) => handleChange('addressLine1', e.target.value)}
              />
              {errors.addressLine1 && <p className="text-sm text-red-400 mt-1">{errors.addressLine1}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Address Line 2</label>
              <input
                type="text"
                className="theme-input w-full p-2 border rounded-lg"
                value={formData.addressLine2}
                onChange={(e) => handleChange('addressLine2', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">City <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
                {errors.city && <p className="text-sm text-red-400 mt-1">{errors.city}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">County <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.county}
                  onChange={(e) => handleChange('county', e.target.value)}
                />
                {errors.county && <p className="text-sm text-red-400 mt-1">{errors.county}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Postcode <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className="theme-input w-full p-2 border rounded-lg"
                  value={formData.postcode}
                  onChange={(e) => handleChange('postcode', e.target.value)}
                />
                {errors.postcode && <p className="text-sm text-red-400 mt-1">{errors.postcode}</p>}
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Modality <span className="text-red-400">*</span></h3>
              <div className="grid grid-cols-2 gap-2">
                {modalityLookups.map((lookup) => (
                  <label key={lookup.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.modality.includes(lookup.value)}
                      onChange={(e) => {
                        const newModality = e.target.checked
                          ? [...formData.modality, lookup.value]
                          : formData.modality.filter(m => m !== lookup.value);
                        handleChange('modality', newModality);
                      }}
                      className="rounded"
                    />
                    <span className="theme-text-primary">{lookup.displayName}</span>
                  </label>
                ))}
              </div>
              {errors.modality && <p className="text-sm text-red-400 mt-2">{errors.modality}</p>}
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Session Types <span className="text-red-400">*</span></h3>
              <div className="grid grid-cols-2 gap-2">
                {sessionTypesLookups.map((lookup) => (
                  <label key={lookup.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.sessionTypes.includes(lookup.value)}
                      onChange={(e) => {
                        const newSessionTypes = e.target.checked
                          ? [...formData.sessionTypes, lookup.value]
                          : formData.sessionTypes.filter(s => s !== lookup.value);
                        handleChange('sessionTypes', newSessionTypes);
                      }}
                      className="rounded"
                    />
                    <span className="theme-text-primary">{lookup.displayName}</span>
                  </label>
                ))}
              </div>
              {errors.sessionTypes && <p className="text-sm text-red-400 mt-2">{errors.sessionTypes}</p>}
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Availability Time Slots</h3>
              <p className="text-sm theme-text-secondary mb-3">Specify exact days and times when the counsellor is available for sessions</p>
              <AvailabilitySlotEditor
                slots={formData.availabilitySlots}
                onChange={(slots: AvailabilitySlot[]) => handleChange('availabilitySlots', slots)}
              />
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">General Availability (Optional)</h3>
              <p className="text-sm theme-text-secondary mb-3">General availability preferences - use time slots above for specific scheduling</p>
              <div className="grid grid-cols-2 gap-2">
                {availabilityLookups.map((lookup) => (
                  <label key={lookup.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.availability.includes(lookup.value)}
                      onChange={(e) => {
                        const newAvailability = e.target.checked
                          ? [...formData.availability, lookup.value]
                          : formData.availability.filter(a => a !== lookup.value);
                        handleChange('availability', newAvailability);
                      }}
                      className="rounded"
                    />
                    <span className="theme-text-primary">{lookup.displayName}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Experience/Training (Optional)</h3>
              <div className="grid grid-cols-2 gap-2">
                {experienceTrainingLookups.map((lookup) => (
                  <label key={lookup.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.experienceOrTraining.includes(lookup.value)}
                      onChange={(e) => {
                        const newExperience = e.target.checked
                          ? [...formData.experienceOrTraining, lookup.value]
                          : formData.experienceOrTraining.filter(ex => ex !== lookup.value);
                        handleChange('experienceOrTraining', newExperience);
                      }}
                      className="rounded"
                    />
                    <span className="theme-text-primary">{lookup.displayName}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Languages</label>
              <div className="grid grid-cols-2 gap-2">
                {languageLookups.map((lookup) => (
                  <label key={lookup._id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.languages.includes(lookup.value)}
                      onChange={(e) => {
                        const newLanguages = e.target.checked
                          ? [...formData.languages, lookup.value]
                          : formData.languages.filter(l => l !== lookup.value);
                        handleChange('languages', newLanguages);
                      }}
                      className="form-checkbox h-4 w-4 text-blue-600"
                    />
                    <span className="theme-text-primary">{lookup.displayName}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Nationality & Work Permit</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Nationality <span className="text-red-400">*</span></label>
                  <select
                    className="theme-input w-full p-2 border rounded-lg"
                    value={formData.nationality}
                    onChange={(e) => handleChange('nationality', e.target.value)}
                  >
                    <option value="">Select nationality</option>
                    {nationalities.map((nat) => (
                      <option key={nat} value={nat}>{nat}</option>
                    ))}
                  </select>
                  {errors.nationality && <p className="text-sm text-red-400 mt-1">{errors.nationality}</p>}
                </div>
                <div className="flex items-center">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.workPermit}
                      onChange={(e) => handleChange('workPermit', e.target.checked)}
                      className="rounded"
                    />
                    <span className="theme-text-primary">Requires Work Permit</span>
                  </label>
                </div>
              </div>
              {formData.workPermit && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium theme-text-primary mb-2">Work Permit Details <span className="text-red-400">*</span></label>
                    <textarea
                      className="theme-input w-full p-2 border rounded-lg"
                      rows={3}
                      value={formData.workPermitDetails || ''}
                      onChange={(e) => handleChange('workPermitDetails', e.target.value)}
                      placeholder="Please provide details of your work permit status"
                    />
                    {errors.workPermitDetails && <p className="text-sm text-red-400 mt-1">{errors.workPermitDetails}</p>}
                  </div>
                  {renderFileUpload('workPermitDocument', 'Work Permit Document', true)}
                </>
              )}
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">DBS Information</h3>
              <div className="mb-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.dbsCheck}
                    onChange={(e) => handleChange('dbsCheck', e.target.checked)}
                    className="rounded"
                  />
                  <span className="theme-text-primary">Has DBS Check</span>
                </label>
              </div>

              {formData.dbsCheck && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium theme-text-primary mb-2">DBS Verification Method <span className="text-red-400">*</span></label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.dbsType === 'certificate'}
                          onChange={() => handleChange('dbsType', 'certificate')}
                          className="rounded"
                        />
                        <span className="theme-text-primary">DBS Certificate</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.dbsType === 'update_service'}
                          onChange={() => handleChange('dbsType', 'update_service')}
                          className="rounded"
                        />
                        <span className="theme-text-primary">DBS Update Service</span>
                      </label>
                    </div>
                    {errors.dbsType && <p className="text-sm text-red-400 mt-1">{errors.dbsType}</p>}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium theme-text-primary mb-2">DBS Number <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      className="theme-input w-full p-2 border rounded-lg"
                      value={formData.dbsNumber}
                      onChange={(e) => handleChange('dbsNumber', e.target.value)}
                    />
                    {errors.dbsNumber && <p className="text-sm text-red-400 mt-1">{errors.dbsNumber}</p>}
                  </div>

                  {formData.dbsType === 'certificate' && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium theme-text-primary mb-2">DBS Issue Date <span className="text-red-400">*</span></label>
                        <DatePickerWrapper>
                          <DatePicker
                            selected={formData.dbsIssueDate ? new Date(formData.dbsIssueDate) : null}
                            onChange={(date) => handleChange('dbsIssueDate', date ? toDateOnlyString(date) : '')}
                            className="theme-input w-full p-2 border rounded-lg"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select issue date (from certificate)"
                            maxDate={new Date()}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                          />
                        </DatePickerWrapper>
                        {errors.dbsIssueDate && <p className="text-sm text-red-400 mt-1">{errors.dbsIssueDate}</p>}
                        <p className="text-xs theme-text-secondary mt-1">Enter the issue/print date from your DBS certificate. Expiry will be calculated automatically (3 years).</p>
                      </div>
                      {renderFileUpload('dbsCertificate', 'DBS Certificate', true)}
                    </>
                  )}

                  {formData.dbsType === 'update_service' && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium theme-text-primary mb-2">DBS Update Service ID <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          className="theme-input w-full p-2 border rounded-lg"
                          value={formData.dbsUpdateServiceId || ''}
                          onChange={(e) => handleChange('dbsUpdateServiceId', e.target.value)}
                        />
                        {errors.dbsUpdateServiceId && <p className="text-sm text-red-400 mt-1">{errors.dbsUpdateServiceId}</p>}
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-sm theme-text-secondary mb-3">
                          If the name on the DBS certificate differs from the counsellor's preferred name, please enter their legal name as it appears on the certificate:
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium theme-text-primary mb-2">Legal First Name (Optional)</label>
                            <input
                              type="text"
                              className="theme-input w-full p-2 border rounded-lg"
                              value={formData.legalFirstName || ''}
                              onChange={(e) => handleChange('legalFirstName', e.target.value)}
                              placeholder="Only if different from preferred name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium theme-text-primary mb-2">Legal Last Name (Optional)</label>
                            <input
                              type="text"
                              className="theme-input w-full p-2 border rounded-lg"
                              value={formData.legalLastName || ''}
                              onChange={(e) => handleChange('legalLastName', e.target.value)}
                              placeholder="Only if different from preferred name"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.dbsUpdateServiceConsent || false}
                            onChange={(e) => handleChange('dbsUpdateServiceConsent', e.target.checked)}
                            className="rounded"
                          />
                          <span className="theme-text-primary">Consent to DBS Update Service check <span className="text-red-400">*</span></span>
                        </label>
                        {errors.dbsUpdateServiceConsent && <p className="text-sm text-red-400 mt-1">{errors.dbsUpdateServiceConsent}</p>}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Professional Insurance</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Insurance Provider <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className="theme-input w-full p-2 border rounded-lg"
                    value={formData.insuranceProvider}
                    onChange={(e) => handleChange('insuranceProvider', e.target.value)}
                  />
                  {errors.insuranceProvider && <p className="text-sm text-red-400 mt-1">{errors.insuranceProvider}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Insurance Policy Number <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className="theme-input w-full p-2 border rounded-lg"
                    value={formData.insuranceNumber}
                    onChange={(e) => handleChange('insuranceNumber', e.target.value)}
                  />
                  {errors.insuranceNumber && <p className="text-sm text-red-400 mt-1">{errors.insuranceNumber}</p>}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium theme-text-primary mb-2">Insurance Expiry Date <span className="text-red-400">*</span></label>
                <DatePickerWrapper>
                  <DatePicker
                    selected={formData.insuranceExpiryDate ? new Date(formData.insuranceExpiryDate) : null}
                    onChange={(date) => handleChange('insuranceExpiryDate', date ? toDateOnlyString(date) : '')}
                    className="theme-input w-full p-2 border rounded-lg"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select expiry date"
                    minDate={new Date()}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </DatePickerWrapper>
                {errors.insuranceExpiryDate && <p className="text-sm text-red-400 mt-1">{errors.insuranceExpiryDate}</p>}
              </div>
              {renderFileUpload('insuranceCertificate', 'Insurance Certificate', true)}
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Online Learning Certificate (Optional)</h3>
              {renderFileUpload('onlineLearningCertificate', 'Online Learning Certificate', false)}
            </div>

            <div className="p-4 border rounded-lg theme-input">
              <h3 className="text-lg font-semibold theme-text-primary mb-3">Trust ID Verification Document (Optional)</h3>
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm theme-text-secondary">
                  If you have the Trust ID verification document, you can upload it now. Otherwise, you can send the verification link to the counsellor after creating their record.
                </p>
              </div>
              {renderFileUpload('trustIdDocument', 'Trust ID Verification Document', false)}
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-2">Notes for Trust ID Verification</label>
              <textarea
                className="theme-input w-full p-2 border rounded-lg"
                rows={3}
                value={formData.trustIdNotes || ''}
                onChange={(e) => handleChange('trustIdNotes', e.target.value)}
                placeholder="Optional notes for the Trust ID verification process"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center">
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                      onClick={() => navigate('/counsellor-management')}
                    >
                      Back to Home
                    </Button>
                  </div>

                  <div className="text-right">
                    <Typography 
                      variant="h3" 
                      component="h1" 
                      gutterBottom
                      className="theme-text-primary"
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 800,
                        fontSize: '2.5rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Counsellor Creation
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto py-8">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-8">
                  {steps.map((label, index) => (
                    <div key={label} className="flex items-center flex-1">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          index === activeStep 
                            ? 'bg-blue-500 text-white' 
                            : index < activeStep 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-300 text-gray-600'
                        }`}>
                          {index + 1}
                        </div>
                        <span className={`ml-3 theme-text-primary ${index === activeStep ? 'font-semibold' : ''}`}>
                          {label}
                        </span>
                      </div>
                      {index < steps.length - 1 && (
                        <div className="flex-1 h-1 mx-4 bg-gray-300">
                          <div 
                            className={`h-full ${index < activeStep ? 'bg-green-500' : 'bg-gray-300'}`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {renderStepContent(activeStep)}

              <div className="flex justify-between mt-8">
                <Button
                  onClick={() => navigate('/counsellor-management')}
                  disabled={submitting}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  CANCEL
                </Button>
                
                <div className="flex gap-2">
                  {activeStep > 0 && (
                    <Button 
                      onClick={handleBack} 
                      disabled={submitting}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700
                      }}
                    >
                      Back
                    </Button>
                  )}
                  
                  {activeStep < steps.length - 1 ? (
                    <Button 
                      onClick={handleNext} 
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '120px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                    >
                      NEXT
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      disabled={submitting}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                    >
                      {submitting ? 'Creating...' : 'Create Counsellor'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
};

export default AddExistingCounsellor;
